import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Skeleton,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { CashkaroService } from "../../../core/services";
import { CashkaroCampaignSummary, CashkaroCategory } from "./types";
import { CC, categoryColor, categoryIcon, CashkaroOutletContext } from "./CashkaroLayout";
import { CashkaroCampaignPreviewDialog } from "./CashkaroCampaignPreviewDialog";
import { ChevronRightIcon, ChevronLeftIcon } from "lucide-react";

// A horizontal-scroll row with its scrollbar hidden — shared by the desktop
// category rail and both new mobile-only rails (category, brand). The mobile
// campaign carousel deliberately does NOT use this: it keeps its native
// scrollbar visible as an explicit scroll affordance.
const hiddenScrollbarSx = {
  scrollbarWidth: "none" as const,
  "&::-webkit-scrollbar": { display: "none" },
};

// Trackier's raw category taxonomy has two near-duplicate values for the same
// bucket — both fold into one "Others" tile in the UI. The backend has no
// category literally named "Others", so selecting this tile can't be sent
// straight through as a `category` filter param (see fetchCampaigns): it's
// fetched unfiltered and narrowed client-side by these raw names instead.
const OTHERS_LABEL = "Others";
const OTHERS_RAW_NAMES = ["miscellaneous", "miscellaneousness"];

// Tile-card for the DESKTOP category rail — icon badge + name + count, centered,
// with a colored-glow active/hover treatment. Untouched by the mobile redesign
// below (rendered only at sm+).
const CategoryTile: React.FC<{
  label: string;
  count: number;
  color: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}> = ({
  label,
  count,
  color,
  icon,
  active,
  onClick,
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      all: "unset",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      cursor: "pointer",
      borderRadius: CC.radiusMd,
      p: 1.25,
      fontFamily: CC.font,

      flex: "0 0 152px",
      maxWidth: 152,
      minWidth: 152,

      border: `1px solid ${active ? CC.primary : CC.border}`,
      background: active ? CC.primaryWash : CC.card,

      boxShadow: active
        ? `0 4px 16px ${color}33`
        : "0 1px 2px rgba(15,23,42,.04)",

      transition:
        "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease",

      "&:hover": {
        transform: "translateY(-3px)",
        borderColor: CC.primary,
        boxShadow: `0 6px 18px ${color}26`,
      },
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.1rem",
        mb: 0.5,

        bgcolor: active ? "#fff" : `${color}1a`,

        boxShadow: active
          ? `0 0 0 4px ${color}22`
          : "none",

        transition:
          "background 0.15s ease, box-shadow 0.15s ease",
      }}
    >
      {icon}
    </Box>

    {/* Label */}
    <Box
      sx={{
        fontWeight: 700,
        fontSize: "0.8rem",
        textTransform: "capitalize",
        color: active ? CC.primaryDark : CC.textPrimary,
        lineHeight: 1.2,
        transition: "color 0.15s ease",
      }}
    >
      {label}
    </Box>

    {/* Count */}
    <Box
      sx={{
        fontSize: "0.66rem",
        fontWeight: 600,
        color: CC.textSecondary,
        mt: 0.15,
      }}
    >
      {count} {count === 1 ? "offer" : "offers"}
    </Box>
  </Box>
);

// Mobile-only "Shop by category" tile — a plain square icon + label, no card
// chrome (border/shadow/count), matching the dense reference layout. Active
// state reads via a colored ring around the square instead of a bordered box.
const CategoryTileMobile: React.FC<{
  label: string;
  color: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, color, icon, active, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      all: "unset",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flex: "0 0 76px",
      width: 76,
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "14px",
        bgcolor: `${color}1a`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.5rem",
        border: `2px solid ${active ? CC.primary : "transparent"}`,
        transition: "border-color 0.15s ease",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: "0.65rem",
        fontWeight: 600,
        fontFamily: CC.font,
        textAlign: "center",
        textTransform: "capitalize",
        lineHeight: 1.2,
        color: active ? CC.primaryDark : CC.textPrimary,
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {label}
    </Typography>
  </Box>
);

// Mobile-only "Shop by brand" tile — just the brand's own logo (it's already a
// wordmark image), no caption needed underneath. If this brand only has one
// campaign, tapping goes straight to its page (same request-access-then-
// navigate flow as "Get My Link"); if it has several (see the thumbnail-match
// grouping in the parent), tapping instead filters the campaign grid below
// to just this brand's — `active` reflects that filtered state.
const BrandLogoTile: React.FC<{ thumbnail: string | null; title: string; active: boolean; onClick: () => void }> = ({ thumbnail, title, active, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      all: "unset",
      cursor: "pointer",
      flex: "0 0 64px",
      width: 64,
      height: 64,
      borderRadius: CC.radiusSm,
      bgcolor: "#fff",
      border: `2px solid ${active ? CC.primary : CC.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    {thumbnail ? (
      <Box sx={{ width: "100%", height: "100%", p: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box component="img" src={thumbnail} alt={title} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
      </Box>
    ) : (
      <StorefrontOutlinedIcon sx={{ fontSize: 22, color: "#c7cad1" }} />
    )}
  </Box>
);

// Mobile-only campaign "card" for the 3-column wrapping grid below — narrow,
// everything condensed to fit: logo, clamped title, payout, a short-label
// CTA. Same handlers/state as the desktop grid card, just re-laid-out and
// re-worded for the width.
const CampaignGridCard: React.FC<{
  campaign: CashkaroCampaignSummary;
  payoutLabel: string;
  isRejected: boolean;
  isJoining: boolean;
  onOpen: () => void;
  onCta: (e: React.MouseEvent) => void;
}> = ({ campaign, payoutLabel, isRejected, isJoining, onOpen, onCta }) => {
  const ctaLabel = isRejected ? "Rejected" : isJoining ? "…" : "Get Link";
  return (
    <Box
      onClick={onOpen}
      sx={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        bgcolor: CC.card,
        border: `1px solid #f6f6f6`,
        borderRadius: CC.radiusMd,
        p: 1,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {campaign.has_generated_link && (
        <Chip
          label="Joined"
          size="small"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            height: 16,
            fontSize: 8,
            fontWeight: 700,
            fontFamily: CC.font,
            borderRadius: "999px",
            bgcolor: CC.primaryWash,
            color: CC.primaryDark,
            "& .MuiChip-label": { px: 0.6 },
          }}
        />
      )}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: CC.radiusSm,
          bgcolor: "#fff",
          border: `1px solid transparent`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          mb: 0.75,
        }}
      >
        {campaign.thumbnail ? (
          <Box sx={{ width: "100%", height: "100%", p: 0.25, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box component="img" src={campaign.thumbnail} alt={campaign.title} sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", border:'none'}} />
          </Box>
        ) : (
          <StorefrontOutlinedIcon sx={{ fontSize: 26, color: "#c7cad1" }} />
        )}
      </Box>
      <Typography
        title={campaign.title}
        sx={{
          fontWeight: 700,
          fontSize: "0.72rem",
          color: CC.textPrimary,
          fontFamily: CC.font,
          lineHeight: 1.2,
          minHeight: "2.4em",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {campaign.title}
      </Typography>
      <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.66rem", color: CC.accent, fontFamily: CC.font, mt: 0.25, mb: 0.75 }}>
        {payoutLabel}
      </Typography>
      <Button
        fullWidth
        disableElevation
        size="small"
        disabled={isRejected || isJoining}
        onClick={onCta}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 10,
          fontFamily: CC.font,
          borderRadius: "999px",
          px: 0.5,
          py: 0.4,
          minWidth: 0,
          ...(isRejected
            ? {
                border: `1px solid ${CC.border}`,
                color: CC.textSecondary,
                "&.Mui-disabled": { color: CC.textSecondary, border: `1px solid ${CC.border}` },
              }
            : {
                bgcolor: "#000",
                color: "#fff",
                "&:hover": { bgcolor: "#111" },
                "&.Mui-disabled": { bgcolor: "#000", color: "#fff", opacity: 0.7 },
              }),
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 700, fontSize: "1rem", fontFamily: CC.font, color: CC.textPrimary, mb: 1, mt: 2.5 }}>
    {children}
  </Typography>
);

const CardSkeleton: React.FC = () => (
  <Grid item xs={12} sm={6} md={4}>
    <Box sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: CC.radiusSm, flexShrink: 0 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton width="70%" height={22} />
          <Skeleton width="40%" />
        </Box>
      </Box>
      <Skeleton variant="rounded" width="50%" height={20} sx={{ borderRadius: "999px", mb: 1 }} />
      <Skeleton variant="rounded" height={32} sx={{ borderRadius: "999px" }} />
    </Box>
  </Grid>
);

export const CashkaroCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.UserReducer?.UserData);
  const influencerId = user?.influencer_id;
  const accountId = user?.account_id;

  const { search } = useOutletContext<CashkaroOutletContext>();
  const [campaigns, setCampaigns] = useState<CashkaroCampaignSummary[]>([]);
  const [categories, setCategories] = useState<CashkaroCategory[]>([]);
  const [totalCampaignCount, setTotalCampaignCount] = useState(0);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [joiningId, setJoiningId] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "info" | "error" } | null>(null);
  // Mobile "Shop by brand" drill-down — there's no real brand id in the data
  // (each row is just one Trackier campaign), so grouping "campaigns from the
  // same brand" can only go on an exact thumbnail-URL match. Cleared whenever
  // the category/search filters change, since it's a temporary narrowing of
  // whatever's currently listed, not an independent filter of its own.
  const [selectedBrandThumbnail, setSelectedBrandThumbnail] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    if (!influencerId) return;
    setLoading(true);
    try {
      // "Others" isn't a real backend category (see OTHERS_RAW_NAMES above) —
      // fetch unfiltered and narrow client-side by the actual raw category
      // names instead of sending a category value the server won't match.
      const isOthers = category === OTHERS_LABEL;
      const res = await CashkaroService.getCampaigns({ influencer_id: influencerId, search, category: isOthers ? "" : category });
      const list = res?.data?.campaigns;
      const campaignsList: CashkaroCampaignSummary[] = Array.isArray(list) ? list : [];
      setCampaigns(
        isOthers
          ? campaignsList.filter((c) => (Array.isArray(c.categories) ? c.categories : []).some((cat) => OTHERS_RAW_NAMES.includes((cat || "").toLowerCase())))
          : campaignsList
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    CashkaroService.getCategories()
      .then((res) => {
        const list = res?.data?.categories;
        setCategories(Array.isArray(list) ? list : []);
        setTotalCampaignCount(Number(res?.data?.total_campaigns) || 0);
      })
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchCampaigns, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [influencerId, search, category]);

  useEffect(() => {
    setSelectedBrandThumbnail(null);
  }, [category, search]);

  // No separate "join campaign" step, and no direct link generation from this
  // card either: if access isn't approved yet, silently request it (this
  // live-probes Trackier — see backend Cashkaro.requestCampaignAccess), then
  // land the influencer on the campaign's own full page either way — that's
  // where they pick a product (or the whole site) and actually generate the
  // link, so it comes with a label, product context, and confirmation instead
  // of a link that's just silently copied to their clipboard.
  const handleCampaignAction = async (campaign: CashkaroCampaignSummary) => {
    if (campaign.access_status === "rejected") return;
    if (joiningId === campaign.trackier_id) return;

    if (campaign.access_status === "approved") {
      navigate(`/influencer/brand-deals/campaigns/${campaign.trackier_id}`);
      return;
    }

    setJoiningId(campaign.trackier_id);
    try {
      const accessRes = await CashkaroService.requestCampaignAccess(campaign.trackier_id, influencerId);
      const status = accessRes?.data?.access_status;
      if (status !== campaign.access_status) await fetchCampaigns();
      if (status === "approved") {
        navigate(`/influencer/brand-deals/campaigns/${campaign.trackier_id}`);
      } else {
        setSnackbar({
          message: "This campaign is still awaiting approval — we'll unlock your link as soon as it's ready.",
          severity: "info",
        });
      }
    } catch {
      setSnackbar({ message: "Could not check campaign access right now — please try again.", severity: "error" });
    } finally {
      setJoiningId(null);
    }
  };

  // Trackier's raw categories include two near-duplicates (see OTHERS_RAW_NAMES)
  // that should read as a single "Others" tile — merge those into one entry
  // with a summed count; every other category passes through unchanged.
  const categoryOptions = useMemo<CashkaroCategory[]>(() => {
    if (!Array.isArray(categories)) return [];

    const merged: CashkaroCategory[] = [];
    for (const item of categories) {
      const name = OTHERS_RAW_NAMES.includes((item.name || "").toLowerCase()) ? OTHERS_LABEL : item.name;
      const existing = merged.find((m) => m.name === name);
      if (existing) {
        existing.campaign_count += item.campaign_count;
      } else {
        merged.push({ name, campaign_count: item.campaign_count });
      }
    }
    return merged;
  }, [categories]);

  const payoutLabelFor = (campaign: CashkaroCampaignSummary) =>
    campaign.payout_amount != null
      ? campaign.payout_model === "percentage"
        ? `${campaign.payout_amount}% commission`
        : `${campaign.currency || ""} ${campaign.payout_amount} payout`
      : "Payout varies";

  // Mobile "Shop by brand" tap: single-campaign brands (the common case) jump
  // straight to that campaign's page, same as tapping a card's CTA. A brand
  // with more than one campaign (same thumbnail) instead filters the campaign
  // carousel below to just those — tapping the same brand again clears it.
  const handleBrandTileClick = (campaign: CashkaroCampaignSummary) => {
    const siblings = campaign.thumbnail ? campaigns.filter((c) => c.thumbnail === campaign.thumbnail) : [campaign];
    if (siblings.length <= 1) {
      handleCampaignAction(campaign);
      return;
    }
    setSelectedBrandThumbnail((prev) => (prev === campaign.thumbnail ? null : campaign.thumbnail));
  };

  const gridCampaigns = selectedBrandThumbnail ? campaigns.filter((c) => c.thumbnail === selectedBrandThumbnail) : campaigns;
  const selectedBrandTitle = selectedBrandThumbnail ? gridCampaigns[0]?.title : null;

  return (
    <Box>
      {/* ================= MOBILE ONLY ================= */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <SectionHeading>Categories</SectionHeading>
        <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 0.5, ...hiddenScrollbarSx }}>
          <CategoryTileMobile label="All" color={CC.primary} icon="🗂️" active={category === ""} onClick={() => setCategory("")} />
          {categoryOptions.map((c) => (
            <CategoryTileMobile
              key={c.name}
              label={c.name}
              color={categoryColor(c.name)}
              icon={categoryIcon(c.name)}
              active={category === c.name}
              onClick={() => setCategory(category === c.name ? "" : c.name)}
            />
          ))}
        </Box>

        {!loading && campaigns.length > 0 && (
          <>
            <SectionHeading>Brands</SectionHeading>
            <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 0.5, ...hiddenScrollbarSx }}>
              {campaigns.map((c) => (
                <BrandLogoTile
                  key={c.trackier_id}
                  thumbnail={c.thumbnail}
                  title={c.title}
                  active={!!selectedBrandThumbnail && c.thumbnail === selectedBrandThumbnail}
                  onClick={() => handleBrandTileClick(c)}
                />
              ))}
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <SectionHeading>{selectedBrandTitle ? `${selectedBrandTitle} campaigns` : "All campaigns"}</SectionHeading>
          {selectedBrandThumbnail && (
            <Typography
              component="button"
              onClick={() => setSelectedBrandThumbnail(null)}
              sx={{ all: "unset", cursor: "pointer", color: CC.primary, fontWeight: 600, fontSize: "0.8rem", fontFamily: CC.font }}
            >
              Clear
            </Typography>
          )}
        </Box>
        {loading ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" sx={{ height: 168, borderRadius: CC.radiusMd }} />
            ))}
          </Box>
        ) : gridCampaigns.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 36, color: "#c7cad1", mb: 1 }} />
            <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, fontSize: "0.875rem" }}>No campaigns match your filters.</Typography>
          </Box>
        ) : (
          // Wraps into rows of 3 — this is the exhaustive "browse everything"
          // list, so it scrolls vertically with the page rather than sideways
          // like the Categories/Brands shelves above it.
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25, pb: 1.5 }}>
            {gridCampaigns.map((campaign) => (
              <CampaignGridCard
                key={campaign.trackier_id}
                campaign={campaign}
                payoutLabel={payoutLabelFor(campaign)}
                isRejected={campaign.access_status === "rejected"}
                isJoining={joiningId === campaign.trackier_id}
                onOpen={() => setPreviewId(campaign.trackier_id)}
                onCta={(e) => {
                  e.stopPropagation();
                  handleCampaignAction(campaign);
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ================= DESKTOP / TABLET (sm+) — unchanged ================= */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box sx={{ position: "relative", mb: 2 }}>
          {/* Left Arrow */}
          <IconButton
            onClick={() => {
              document.getElementById("category-scroll")?.scrollBy({
                left: -250,
                behavior: "smooth",
              });
            }}
            sx={{
              position: "absolute",
              left: -16,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 34,
              height: 34,
              bgcolor: "#fff",
              border: `1px solid ${CC.border}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              "&:hover": {
                bgcolor: "#fff",
              },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          {/* Categories */}
          <Box
            id="category-scroll"
            sx={{
              display: "flex",
              gap: 1.25,
              overflowX: "auto",
              overflowY: "hidden",
              scrollBehavior: "smooth",
              px: 1,
              py: 1,
              ...hiddenScrollbarSx,
            }}
          >
            <CategoryTile
              label="All"
              count={totalCampaignCount}
              color={CC.primary}
              icon="🗂️"
              active={category === ""}
              onClick={() => setCategory("")}
            />

            {categoryOptions.map((c) => (
              <CategoryTile
                key={c.name}
                label={c.name}
                count={c.campaign_count}
                color={categoryColor(c.name)}
                icon={categoryIcon(c.name)}
                active={category === c.name}
                onClick={() =>
                  setCategory(category === c.name ? "" : c.name)
                }
              />
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            onClick={() => {
              document.getElementById("category-scroll")?.scrollBy({
                left: 250,
                behavior: "smooth",
              });
            }}
            sx={{
              position: "absolute",
              right: -16,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 34,
              height: 34,
              bgcolor: "#fff",
              border: `1px solid ${CC.border}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              "&:hover": {
                bgcolor: "#fff",
              },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading ? (
          <Grid container spacing={1.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </Grid>
        ) : campaigns.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 40, color: "#c7cad1", mb: 1 }} />
            <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font }}>No campaigns match your filters.</Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {campaigns.map((campaign) => {
              const isRejected = campaign.access_status === "rejected";
              const payoutLabel = payoutLabelFor(campaign);
              const ctaLabel = isRejected ? "Access Rejected" : joiningId === campaign.trackier_id ? "Getting access…" : "Get My Link";
              const handleCtaClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                handleCampaignAction(campaign);
              };
              return (
                <Grid item xs={12} sm={6} md={4} key={campaign.trackier_id}>
                  <Card
                    elevation={0}
                    onClick={() => setPreviewId(campaign.trackier_id)}
                    sx={{
                      position: "relative",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: CC.radiusLg,
                      border: `1px solid ${CC.border}`,
                      bgcolor: CC.card,
                      overflow: "hidden",
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(15,23,42,.06)",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                      "&:hover": { borderColor: CC.borderHover, boxShadow: "0 12px 24px rgba(15,23,42,.10)", transform: "translateY(-4px)" },
                    }}
                  >
                    {campaign.has_generated_link && (
                      <Chip
                        label="Joined"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          fontWeight: 700,
                          fontFamily: CC.font,
                          height: 22,
                          fontSize: 11,
                          borderRadius: "999px",
                          bgcolor: CC.primaryWash,
                          color: CC.primaryDark,
                          zIndex: 1,
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1, p: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        {/* Fixed-size logo tile on a WHITE ground (not the page's gray surface) —
                            most brand logos already assume a white background, and a small,
                            consistent avatar avoids the size/shape mismatch a full-width banner
                            creates across very differently-shaped source logos (ovals, circles,
                            wordmarks, rectangles). */}
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: CC.radiusSm,
                            bgcolor: "#fff",
                            border: `1px solid ${CC.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            overflow: "hidden",
                          }}
                        >
                          {campaign.thumbnail ? (
                            <Box sx={{ width: "100%", height: "100%", p: 0.75, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <CardMedia
                                component="img"
                                image={campaign.thumbnail}
                                alt={campaign.title}
                                sx={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain", border:'1px solid transparent', borderRadius:'10px'}}
                              />
                            </Box>
                          ) : (
                            <StorefrontOutlinedIcon sx={{ fontSize: 26, color: "#c7cad1" }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            noWrap
                            title={campaign.title}
                            sx={{ color: CC.textPrimary, fontSize: "0.95rem", fontFamily: CC.font }}
                          >
                            {campaign.title}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                            {(Array.isArray(campaign.categories) ? campaign.categories : []).slice(0, 2).map((cat) => (
                              <Chip
                                key={cat}
                                label={cat}
                                size="small"
                                sx={{
                                  textTransform: "capitalize",
                                  height: 20,
                                  fontSize: 11,
                                  fontFamily: CC.font,
                                  borderRadius: "999px",
                                  bgcolor: `${categoryColor(cat)}1a`,
                                  color: categoryColor(cat),
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Payout as a bold pill badge — the headline number on the card,
                          not a plain line of colored text. */}
                      <Chip
                        label={payoutLabel}
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          fontWeight: 700,
                          fontSize: 11.5,
                          fontFamily: CC.font,
                          height: 22,
                          borderRadius: "999px",
                          bgcolor: CC.accentWash,
                          color: CC.accent,
                        }}
                      />

                      <Box sx={{ mt: "auto", pt: 0.5 }}>
                        <Button
                          fullWidth
                          disableElevation
                          size="small"
                          startIcon={joiningId === campaign.trackier_id ? undefined : <LinkOutlinedIcon fontSize="small" />}
                          disabled={isRejected || joiningId === campaign.trackier_id}
                          onClick={handleCtaClick}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontFamily: CC.font,
                            borderRadius: "999px",
                            py: 0.65,
                            ...(isRejected
                              ? {
                                  border: `1px solid ${CC.border}`,
                                  color: CC.textSecondary,
                                  "&.Mui-disabled": { color: CC.textSecondary, border: `1px solid ${CC.border}` },
                                }
                              : {
                                  bgcolor: "#000",
                                  color: "#fff",
                                  "&:hover": { bgcolor: "#111" },
                                  "&.Mui-disabled": { bgcolor: "#000", color: "#fff", opacity: 0.7 },
                                }),
                          }}
                        >
                          {ctaLabel}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <CashkaroCampaignPreviewDialog
        campaignId={previewId}
        influencerId={influencerId}
        accountId={accountId}
        open={previewId !== null}
        onClose={() => setPreviewId(null)}
      />

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CashkaroCampaigns;
